import React, { useState } from "react";
import { ArrowLeft, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../Input/Input";
import Notification from "../../components/Notification/Notification";

/* ===================== SERVICES ===================== */
import { apiPost } from "../../apiService";

/* ===================== STYLES ===================== */
import styles from "./CreateApiCredentials.module.scss";

const CreateApiCredentials = ({ onBack }) => {
  /* =========================
     STATE
  ========================= */
  const [env, setEnv] = useState("");
  const [keyName, setKeyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [ipInput, setIpInput] = useState("");
  const [ipList, setIpList] = useState([]);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  const [errors, setErrors] = useState({});

  const companyName = localStorage.getItem("companyName");

  /* =========================
     HELPERS & VALIDATION
  ========================= */
  const ipRegex =
    /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

  const showNotification = (msg, type = "success") => {
    setNotification(msg);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const addIp = () => {
    const trimmedIp = ipInput.trim();

    setErrors((prev) => ({ ...prev, ipAddress: "" }));

    if (!trimmedIp) {
      setErrors((prev) => ({ ...prev, ipAddress: "IP Address is required" }));
      return;
    }

    if (!ipRegex.test(trimmedIp)) {
      setErrors((prev) => ({ ...prev, ipAddress: "Enter a valid IP Address" }));
      return;
    }

    if (ipList.includes(trimmedIp)) {
      setErrors((prev) => ({ ...prev, ipAddress: "IP already added" }));
      return;
    }

    setIpList([...ipList, trimmedIp]);
    setIpInput("");
  };

  const removeIp = (ip) => {
    setIpList(ipList.filter((item) => item !== ip));
  };

  /* =========================
     CREATE CREDENTIALS API
  ========================= */
  const handleCreate = async () => {
    let finalIpList = [...ipList];

    if (ipInput.trim()) {
      const trimmedIp = ipInput.trim();

      if (!ipRegex.test(trimmedIp)) {
        setErrors((prev) => ({
          ...prev,
          ipAddress: "Enter a valid IP Address",
        }));
        return;
      }

      if (!finalIpList.includes(trimmedIp)) {
        finalIpList.push(trimmedIp);
      }
    }

    if (!keyName.trim()) {
      setErrors((prev) => ({ ...prev, keyName: "Key Name is required" }));
      return;
    }
    if (finalIpList.length === 0) {
      setErrors((prev) => ({ ...prev, ipAddress: "IP Address is required" }));
      return;
    }
    if (!env) {
      setErrors((prev) => ({ ...prev, env: "Please select environment" }));
      return;
    }

    const payload = {
      keyName,
      environment: env === "prod" ? "Production" : "PreProduction",
      ipAddress: finalIpList,
      companyName,
    };

    try {
      setLoading(true);

      const res = await apiPost("catalog", "/secrets/generate", payload);

      if (res?.status === "success") {
        showNotification("API Credentials created successfully", "success");
        setTimeout(() => onBack?.(), 800);
      } else {
        throw new Error();
      }
    } catch (err) {
      showNotification(
        err?.response?.data?.message || "Something went wrong",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     JSX
  ========================= */
  return (
    <div className={styles["admin-create-api-cred__page"]}>
      {/* TOAST CONTAINER */}
      {notification && (
        <div className={styles["admin-create-api-cred__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-create-api-cred__header"]}>
        <button
          className={styles["admin-create-api-cred__back-btn"]}
          onClick={onBack}
          aria-label="Go Back"
        >
          <ArrowLeft size={20} strokeWidth={2.5} />
        </button>

        <div className={styles["admin-create-api-cred__title-group"]}>
          <h2 className={styles["admin-create-api-cred__title"]}>
            Create New API Credentials
          </h2>
          <p className={styles["admin-create-api-cred__subtitle"]}>
            Create Auth 2.0 credentials for your application to start
            integrating our APIs.
          </p>
        </div>
      </div>

      {/* MAIN FORM CARD */}
      <div className={styles["admin-create-api-cred__card"]}>
        {/* Key Name */}
        <div className={styles["admin-create-api-cred__form-group"]}>
          <Input
            label="Key Name"
            placeholder="e.g. My Awesome App"
            value={keyName}
            onChange={(e) => {
              setKeyName(e.target.value);
              setErrors({ ...errors, keyName: "" });
            }}
            error={errors.keyName}
            required
          />
        </div>

        {/* IP Address Section */}
        <div className={styles["admin-create-api-cred__form-group"]}>
          <label className={styles["admin-create-api-cred__label"]}>
            IPv4 Address{" "}
            <span className={styles["admin-create-api-cred__required-star"]}>
              *
            </span>
          </label>

          <div className={styles["admin-create-api-cred__ip-row"]}>
            <Input
              placeholder="e.g. 192.168.1.10"
              value={ipInput}
              onChange={(e) => {
                setIpInput(e.target.value);
                setErrors((prev) => ({ ...prev, ipAddress: "" }));
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addIp();
                }
              }}
            />
            <button
              type="button"
              className={styles["admin-create-api-cred__add-btn"]}
              onClick={addIp}
            >
              Add
            </button>
          </div>

          {/* Render IP Chips */}
          {ipList.length > 0 && (
            <div className={styles["admin-create-api-cred__ip-list"]}>
              {ipList.map((ip, index) => (
                <div
                  key={index}
                  className={styles["admin-create-api-cred__ip-chip"]}
                >
                  {ip}
                  <div
                    className={styles["admin-create-api-cred__ip-remove"]}
                    onClick={() => removeIp(ip)}
                  >
                    <X size={12} strokeWidth={3} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {errors.ipAddress && (
            <p className={styles["admin-create-api-cred__error-text"]}>
              {errors.ipAddress}
            </p>
          )}
        </div>

        {/* Environment Selection */}
        <div className={styles["admin-create-api-cred__form-group"]}>
          <label className={styles["admin-create-api-cred__label"]}>
            Environment{" "}
            <span className={styles["admin-create-api-cred__required-star"]}>
              *
            </span>
          </label>

          <div className={styles["admin-create-api-cred__env-grid"]}>
            <label
              className={`${styles["admin-create-api-cred__radio-card"]} ${
                env === "pre"
                  ? styles["admin-create-api-cred__radio-card--active"]
                  : ""
              }`}
            >
              <input
                type="radio"
                name="env"
                value="pre"
                checked={env === "pre"}
                onChange={(e) => {
                  setEnv(e.target.value);
                  setErrors({ ...errors, env: "" });
                }}
              />
              <span>Pre Production</span>
            </label>

            <label
              className={`${styles["admin-create-api-cred__radio-card"]} ${
                env === "prod"
                  ? styles["admin-create-api-cred__radio-card--active"]
                  : ""
              }`}
            >
              <input
                type="radio"
                name="env"
                value="prod"
                checked={env === "prod"}
                onChange={(e) => {
                  setEnv(e.target.value);
                  setErrors({ ...errors, env: "" });
                }}
              />
              <span>Production</span>
            </label>
          </div>

          {errors.env && (
            <p className={styles["admin-create-api-cred__error-text"]}>
              {errors.env}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className={styles["admin-create-api-cred__actions"]}>
          <button
            className={styles["admin-create-api-cred__btn-primary"]}
            onClick={handleCreate}
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Credentials"}
          </button>

          <button
            className={styles["admin-create-api-cred__btn-cancel"]}
            onClick={onBack}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateApiCredentials;
